/**
 * CoverageLine and DistributionBar - the two honest companions of the KPI trio.
 *
 * CoverageLine says how much of the TPIN the counts cover ("n of m branches
 * reporting"); unreachable branches are never zeroed into the counts (the
 * contract invariant), so the line is what keeps the numbers from reading as
 * complete ALL-branch totals. DistributionBar shows the three ready counts as
 * segments weighted by the server value itself - no percentage is computed
 * (the proportion is presentation of approved counts, not a KPI).
 */

import type { SummaryView } from '@domain/tpin'
import { STATUS_WORD, coverageText, distributionOf } from '@domain/tpinDash'

export function CoverageLine({ view }: { readonly view: SummaryView }) {
  return (
    <p className="coverage-line" aria-live="polite" data-testid="coverage-line">
      {view.kind === 'ready' ? coverageText(view.summary) : ''}
    </p>
  )
}

export function DistributionBar({ view }: { readonly view: SummaryView }) {
  if (view.kind !== 'ready') return null
  const counts = view.summary
  const { present, absent } = distributionOf(counts)
  if (present.length === 0) {
    return (
      <p className="dist-caption" data-testid="dist-empty">
        No invoices in this period.
      </p>
    )
  }
  const label = present.map((key) => `${STATUS_WORD[key]} ${counts[key]}`).join(', ')
  return (
    <div className="dist" data-testid="dist">
      <div className="dist-bar" role="img" aria-label={label} data-testid="dist-bar">
        {present.map((key) => (
          <span key={key} className={`dist-seg dist-${key}`} style={{ flexGrow: counts[key] }} data-testid={`dist-seg-${key}`} />
        ))}
      </div>
      <p className="dist-caption" data-testid="dist-caption">
        {absent.map((key) => `No ${STATUS_WORD[key].toLowerCase()} invoices`).join(' · ')}
      </p>
    </div>
  )
}
